import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Command as CommandPrimitive, useCommandState } from 'cmdk';
import { Building2, MapPin } from 'lucide-react';

import { cn } from '@/lib/utils';
import { CommandItem, CommandList } from '@/components/ui/command';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';

/** One place an address search offers: a town, a street or a house number, with its position. */
export interface AddressSuggestion {
    /** As the address register writes it: "Lyon 3e Arrondissement", "Rue Paul Bert 69003 Lyon". */
    label: string;
    city: string;
    postcode: string;
    /** Where it is, for telling namesakes apart: "69, Rhône, Auvergne-Rhône-Alpes". */
    context: string;
    type: 'municipality' | 'street' | 'housenumber' | 'locality';
    lat: number;
    lng: number;
}

export interface AddressSearchResult {
    items: AddressSuggestion[];
    /** The search did not answer: the field still works as plain text. */
    unavailable?: boolean;
}

export interface AddressAutocompleteLabels {
    /** Names the suggestion list for screen readers. */
    list: string;
    searching: string;
    noMatch: string;
    unavailable: string;
    /** Shown for three or four digits: postcode searches only match all five. */
    postcodeHint: string;
}

const DEFAULT_LABELS: AddressAutocompleteLabels = {
    list: 'Address suggestions',
    searching: 'Searching…',
    noMatch: 'No match. Try a postcode or a town.',
    unavailable: 'Suggestions are unavailable right now. You can still type the address.',
    postcodeHint: 'Type all five digits of the postcode.',
};

export interface AddressAutocompleteProps {
    /** Put on the input so a `<Label htmlFor>` names it. */
    id?: string;
    /** The text in the field. */
    value: string;
    onValueChange: (text: string) => void;
    /** A suggestion was chosen: its label is already in the field. */
    onPick: (place: AddressSuggestion) => void;
    /** Where suggestions come from; aborted when the text changes again. */
    search: (query: string, init: { signal: AbortSignal }) => Promise<AddressSearchResult>;
    /** Enter with no suggestion to choose (none shown yet, or the list closed). */
    onSubmit?: () => void;
    placeholder?: string;
    /** Translated texts; English fallbacks otherwise. */
    labels?: Partial<AddressAutocompleteLabels>;
    /** The data source's credit, under the suggestions. */
    attribution?: string;
    disabled?: boolean;
    invalid?: boolean;
    minLength?: number;
    debounceMs?: number;
    limit?: number;
    className?: string;
    'data-testid'?: string;
}

type Status = 'idle' | 'loading' | 'done' | 'unavailable';

const PARTIAL_POSTCODE = /^\d{3,4}$/;
const itemValue = (generation: number, i: number) => `place-${generation}-${i}`;

/** "Lyon 3e Arrondissement" alone says nothing of the postcode; a street's label already has it. */
const primaryLine = (p: AddressSuggestion) => (p.type === 'municipality' && p.postcode ? `${p.label} · ${p.postcode}` : p.label);

/**
 * The field itself. A plain input rather than cmdk's, which overrides `id`
 * and `aria-labelledby` (a `<Label htmlFor>` would stop naming it) and always
 * says it is expanded. cmdk's keyboard handling sits on its root, so ↑/↓/Enter
 * reach it all the same.
 *
 * The highlighted option is read from the page, not from cmdk's
 * `selectedItemId`: cmdk 1.1 works that id out before the options re-render
 * when it highlights the first one itself, so it is missing or a step behind.
 */
const ComboboxInput = ({ open, listboxId, ...props }: React.ComponentProps<'input'> & { open: boolean; listboxId?: string }) => {
    const selected = useCommandState((s) => s.value);
    const [activeId, setActiveId] = useState<string>();
    useLayoutEffect(() => {
        const option = open && listboxId ? document.getElementById(listboxId)?.querySelector('[cmdk-item][aria-selected="true"]') : null;
        setActiveId(option?.id || undefined);
    }, [open, listboxId, selected]);
    return (
        <InputGroupInput
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            aria-activedescendant={open ? activeId : undefined}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            {...props}
        />
    );
};

/**
 * A text field that suggests addresses as you type — a town, a postcode or a
 * street — and hands back the chosen one with its position, so nothing has to
 * be geocoded again on save.
 *
 * Built on the app's Command list (cmdk) in a Popover anchored to the field:
 * ↑/↓ move through the suggestions, Enter picks, Escape closes. Typing waits
 * `debounceMs` before searching and cancels the search before it. The source
 * is the `search` prop, so any address API fits; the texts come in `labels`.
 */
export function AddressAutocomplete({
    id,
    value,
    onValueChange,
    onPick,
    search,
    onSubmit,
    placeholder,
    labels,
    attribution,
    disabled,
    invalid,
    minLength = 3,
    debounceMs = 250,
    limit = 6,
    className,
    'data-testid': testId,
}: AddressAutocompleteProps) {
    const text = { ...DEFAULT_LABELS, ...labels };
    const [items, setItems] = useState<AddressSuggestion[]>([]);
    const [status, setStatus] = useState<Status>('idle');
    // Bumped with every answer, so its suggestions get values of their own and cmdk highlights
    // the first by itself (it only does when the highlighted one goes or none is).
    const [generation, setGeneration] = useState(0);
    const [focused, setFocused] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [listboxId, setListboxId] = useState<string>();

    const anchorRef = useRef<HTMLDivElement>(null);
    const abortRef = useRef<AbortController | null>(null);
    // The text a pick put in the field: no search for it.
    const pickedRef = useRef<string | null>(null);
    // Read at call time, so a parent passing a new function each render does not restart the search.
    const searchRef = useRef(search);
    searchRef.current = search;

    // cmdk names the listbox itself; read its id once the popover has it in the page.
    const listWrapRef = useCallback((el: HTMLDivElement | null) => {
        setListboxId(el?.querySelector('[cmdk-list]')?.id || undefined);
    }, []);

    const query = value.trim();
    const postcodeHint = PARTIAL_POSTCODE.test(query);

    useEffect(() => {
        if (pickedRef.current !== null && value === pickedRef.current) return undefined;
        pickedRef.current = null;
        abortRef.current?.abort();
        abortRef.current = null;
        if (query.length < minLength || PARTIAL_POSTCODE.test(query)) {
            setItems([]);
            setStatus('idle');
            return undefined;
        }
        const timer = setTimeout(() => {
            const controller = new AbortController();
            abortRef.current = controller;
            setStatus('loading');
            searchRef.current(query, { signal: controller.signal })
                .then((res) => {
                    if (controller.signal.aborted) return;
                    const next = (res?.items ?? []).slice(0, limit);
                    setItems(next);
                    setGeneration((g) => g + 1);
                    setStatus(res?.unavailable && !next.length ? 'unavailable' : 'done');
                })
                .catch(() => {
                    if (controller.signal.aborted) return;
                    setItems([]);
                    setStatus('unavailable');
                });
        }, debounceMs);
        return () => clearTimeout(timer);
        // `query` follows `value`; the rest are settings that do not change while typing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Nothing left in flight once the field is gone.
    useEffect(() => () => abortRef.current?.abort(), []);

    const pick = (place: AddressSuggestion) => {
        abortRef.current?.abort();
        abortRef.current = null;
        pickedRef.current = place.label;
        onValueChange(place.label);
        onPick(place);
        setItems([]);
        setStatus('idle');
        setDismissed(true);
    };

    const hasMessage = !items.length && (status !== 'idle' || postcodeHint);
    const open = focused && !dismissed && !disabled && (items.length > 0 || hasMessage);

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // The caret's keys, not the list's: cmdk would jump to the first and last suggestion.
        if (e.key === 'Home' || e.key === 'End') {
            e.stopPropagation();
            return;
        }
        if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && dismissed && items.length) {
            setDismissed(false);
            return;
        }
        if (e.key === 'Escape' && open) {
            setDismissed(true);
            return;
        }
        // cmdk highlights the first suggestion whenever there are some, so Enter always has one to pick then.
        if (e.key === 'Enter' && !(open && items.length)) {
            // cmdk stops Enter from submitting a form; with nothing to choose it is the field's own Enter.
            onSubmit?.();
        }
    };

    return (
        <CommandPrimitive
            shouldFilter={false}
            loop
            vimBindings={false}
            label={text.list}
            className={cn('w-full', className)}
            data-testid={testId}
        >
            <Popover open={open} onOpenChange={(next) => !next && setDismissed(true)}>
                <PopoverAnchor asChild>
                    <div ref={anchorRef}>
                        <InputGroup data-disabled={disabled || undefined}>
                            <InputGroupAddon>
                                <MapPin />
                            </InputGroupAddon>
                            <ComboboxInput
                                id={id}
                                open={open}
                                listboxId={listboxId}
                                value={value}
                                onChange={(e) => {
                                    setDismissed(false);
                                    onValueChange(e.target.value);
                                }}
                                onKeyDown={onKeyDown}
                                onFocus={() => setFocused(true)}
                                onBlur={() => setFocused(false)}
                                placeholder={placeholder}
                                disabled={disabled}
                                aria-invalid={invalid || undefined}
                            />
                            {status === 'loading' && (
                                <InputGroupAddon align="inline-end">
                                    <Spinner aria-label={text.searching} />
                                </InputGroupAddon>
                            )}
                        </InputGroup>
                    </div>
                </PopoverAnchor>
                <PopoverContent
                    align="start"
                    className="w-(--radix-popover-trigger-width) min-w-64 gap-0 p-1"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    onInteractOutside={(e) => {
                        // A click back in the field keeps the list open.
                        if (anchorRef.current?.contains(e.target as Node)) e.preventDefault();
                    }}
                    data-testid={testId ? `${testId}-list` : undefined}
                >
                    {/* Mouse down on a suggestion keeps the focus in the field. */}
                    <div ref={listWrapRef} onMouseDown={(e) => e.preventDefault()}>
                        <CommandList>
                            {items.map((place, i) => (
                                <CommandItem key={itemValue(generation, i)} value={itemValue(generation, i)} onSelect={() => pick(place)} className="items-start py-2">
                                    {place.type === 'municipality' ? <Building2 className="mt-0.5 text-muted-foreground" /> : <MapPin className="mt-0.5 text-muted-foreground" />}
                                    <span className="flex min-w-0 flex-col">
                                        <span className="truncate">{primaryLine(place)}</span>
                                        {place.context && <span className="truncate text-xs text-muted-foreground">{place.context}</span>}
                                    </span>
                                </CommandItem>
                            ))}
                        </CommandList>
                        {!items.length && (
                            <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground" aria-live="polite" data-testid={testId ? `${testId}-status` : undefined}>
                                {postcodeHint ? text.postcodeHint
                                    : status === 'loading' ? <><Spinner aria-hidden />{text.searching}</>
                                    : status === 'unavailable' ? text.unavailable
                                    : text.noMatch}
                            </div>
                        )}
                        {attribution && items.length > 0 && (
                            <p className="border-t px-2 pt-1.5 pb-1 text-[11px] text-muted-foreground">{attribution}</p>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </CommandPrimitive>
    );
}

export default AddressAutocomplete;
